<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';
    const { $listen, $unlisten } = useNuxtApp();

    import { useSitesStore } from '@/stores/sites';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { usePointsOfInterestStore } from '@/stores/points-of-interest';
    import { useToursStore } from '@/stores/tours';
    import { useTrailsStore } from '@/stores/trails';
    import { useEventsStore } from '@/stores/events';
    import { useSponsorsStore } from '@/stores/sponsors';
    import { usePartnersStore } from '@/stores/partners';
    import { useWindowStore } from '@/stores/window';
    import { useHelpersStore } from '@/stores/helpers';
    import { useUserStore } from '@/stores/user';
    import { useNotificationsStore } from '@/stores/notifications';

    import { updateHead } from '@/composables/helpers/updateHead';
    import { pageSeo } from '@/utils/seo';
    // Was `checkTime()` from the now-deleted app/composables/fetchIntervals.ts (Task 10).
    const REFRESH_GATE_SECONDS = 45;
    const checkTime = (storeVal, currentVal) => Math.floor((currentVal - storeVal) / 1000) >= REFRESH_GATE_SECONDS;

    const frontEnd = useRuntimeConfig().public.clientFrontend;
    const route = useRoute();

    // Follows the route, so a page that sets nothing itself still gets its own
    // title, description and canonical on client-side navigation. Every list
    // page used to share one title and description (see pageSeo in
    // utils/seo.ts); detail pages override this with their own updateHead.
    updateHead(() => ({
        ...pageSeo(route.path),
        path: route.path,
    }));

    // Instantiate the stores
    const sitesStore = useSitesStore();
    const neighborhoodsStore = useNeighborhoodsStore();
    const eventsStore = useEventsStore();
    const partnersStore = usePartnersStore();
    const toursStore = useToursStore();
    const sponsorsStore = useSponsorsStore();
    const userStore = useUserStore();
    const notificationStore = useNotificationsStore();

    // not needed on app load
    const pointsOfInterestStore = usePointsOfInterestStore();

    // not needed on homepage
    const trailsStore = useTrailsStore();
    // Exception for the variable year;
    const year = ref(false);

    const helpersStore = useHelpersStore();
    // Seeded here, at setup — which runs on the client during hydration — so
    // the first client-side navigation does not treat data that arrived with
    // the page a moment ago as stale and refetch every collection.
    helpersStore.lastFetch = Date.now();

    const windowStore = useWindowStore();

    // Variables
    const { hasVisitedBefore, initialized } = storeToRefs(userStore);
    const { getUnreadCount, getNotificationsData } = storeToRefs(notificationStore);

    const pathsNoSitesRefresh = ['/sites/', '/sites', '/events', '/events/', '/my-ohc', '/my-ohc/', '/my-ohc/itineraries', '/my-ohc/itineraries/'];
    const pathsNoEventsRefresh = ['/sites/', '/sites', '/itineraries/', '/itineraries', '/neighborhoods'];
    const addToItineraryIsOpen = ref(false);
    const item = ref(null);
    const siteNameForAlert = ref(null);
    const itineraryNameForAlert = ref(null);
    const showNotifications = ref(false);
    const ohcPages = ['/neighborhoods', '/sites', '/itineraries', '/events', '/my-ohc', '/archive', '/login' ];
    const siteAddedAlertIsOpen = ref(false);
    const itineraryAddedWithSiteAlertIsOpen = ref(false);

    // Methods
    const fetchNotifications = async () => {
        notificationStore.fetchNotifications();
    };

    const refreshUserData = async () => {
        userStore.refreshData();
    };

    function isCACPage() {
        let isCACPage = true;

        let { path } = route;
        if (path.endsWith('/')) path = path.slice(0, -1);

        // We assume that sub-pages of the OHC pages are also OHC pages
        if (path == '') isCACPage = false;
        if (ohcPages.some((page) => path.startsWith(page))) {
            isCACPage = false;
        }

        return isCACPage;
    }

    function setSiteAndItineraryNames(values) {
        siteNameForAlert.value = values?.item?.fields?.Site || 'Site Name';
        console.log(`🍤 ~ setSiteAndItineraryNames ~ values:`, values);
        itineraryNameForAlert.value = values?.selected?.name || 'Itinerary Name';
    }

    function openSiteAddedAlert(values) {
        siteAddedAlertIsOpen.value = true;
        setSiteAndItineraryNames(values);
    }

    function closeSiteAddedAlert() {
        siteAddedAlertIsOpen.value = false;
        siteNameForAlert.value = null;
        itineraryNameForAlert.value = null;
    }

    function openItineraryAddedWithSiteAlert(values) {
        addToItineraryIsOpen.value = false;
        itineraryAddedWithSiteAlertIsOpen.value = true;

        itineraryNameForAlert.value = values.itineraryName;
        siteNameForAlert.value = values.siteName;
    }
    function closeItineraryAddedWithSiteAlert() {
        itineraryAddedWithSiteAlertIsOpen.value = false;
        siteNameForAlert.value = null;
        itineraryNameForAlert.value = null;
    }

    function closeEmailSignupModal(type) {
        userStore.updateHasVisitedBefore(true);
    }

    const resize = () => {
        windowStore.windowHeight = window.innerHeight;
        windowStore.windowWidth = window.innerWidth;
    };

    // Lifecycle
    year.value = false;
    neighborhoodsStore.updateNeighborhoods(false);
    sitesStore.updateSites(false);
    eventsStore.toggleArchivedEvents(false);
    partnersStore.toggleArchivedPartners(false);
    toursStore.toggleArchivedTours(false);
    sponsorsStore.toggleArchivedSponsors(false);
    pointsOfInterestStore.toggleArchivedPoints(false);
    trailsStore.toggleArchivedTrails(false);

    const handleOpenGlobalItineraryModal = (val) => {
        item.value = val.value;
        addToItineraryIsOpen.value = true;
        document.body.style.overflow = 'hidden';
    };

    const handleCloseGlobalItineraryModal = () => {
        addToItineraryIsOpen.value = false;
        document.body.style.overflow = 'auto';
    };

    const handleAddedToItineraryAlert = (val2) => {
        openSiteAddedAlert(val2);
    };

    const handleCreatedItineraryWithSite = (val3) => {
        openItineraryAddedWithSiteAlert(val3);
        document.body.style.overflow = 'auto';
    };

    const handleShowNotifications = () => {
        showNotifications.value = true;
        document.body.style.overflow = 'hidden';
    };

    const handleHideNotifications = () => {
        notificationStore.markAsRead();
        showNotifications.value = false;
        document.body.style.overflow = 'auto';
    };

    const handleVisibilityChange = () => {
        if (!document.hidden) {
            console.info('Back');
            fetchNotifications();
            refreshUserData();
        }
    };

    const busHandlers = [
        ['openGlobalItineraryModal', handleOpenGlobalItineraryModal],
        ['closeGlobalItineraryModal', handleCloseGlobalItineraryModal],
        ['addedToItineraryAlert', handleAddedToItineraryAlert],
        ['createdItineraryWithSite', handleCreatedItineraryWithSite],
        ['showNotifications', handleShowNotifications],
        ['hideNotifications', handleHideNotifications],
    ];

    onMounted(() => {
        notificationStore.fetchNotifications();
        userStore.initUserData();

        if (import.meta.client) {
            resize();
            window.addEventListener('resize', resize);

            for (const [event, handler] of busHandlers) $listen(event, handler);

            document.addEventListener('visibilitychange', handleVisibilityChange);
        }
    });

    // A layout unmounts when the app swaps to another one — /archive uses
    // layouts/archives.vue — and none of these were ever removed, so each swap
    // stacked another copy. mitt in particular keeps a subscription until it is
    // explicitly removed. The visibilitychange one was the expensive leak: after
    // N swaps, every tab refocus fired N fetchNotifications + N refreshUserData.
    //
    // Handlers are named rather than inline so there is a reference to remove.
    onUnmounted(() => {
        if (!import.meta.client) return;
        window.removeEventListener('resize', resize);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        for (const [event, handler] of busHandlers) $unlisten(event, handler);
    });

    watch(
        () => route.path,
        async (to, from) => {
            const canRefetch = checkTime(helpersStore.lastFetch, Date.now());
            if (canRefetch) {
                if (!pathsNoSitesRefresh.includes(to)) sitesStore.fetchSites();
                if (!pathsNoEventsRefresh.includes(to)) eventsStore.fetchEvents();
                trailsStore.fetchTrails();
                pointsOfInterestStore.fetchPointsOfInterest();
                neighborhoodsStore.fetchNeighborhoods();
                // No second fetchSites() here: there used to be one, so every
                // refresh downloaded the full site list (~670KB) twice — and on
                // the paths above that deliberately skip it, once anyway.
                helpersStore.lastFetch = Date.now();
            }
            fetchNotifications();
            refreshUserData();
        },
        { deep: true }
    );
</script>

<template>
    <div>
        <ClientOnly>
            <GlobalLocation v-if="userStore.hasLocationPermission" />
        </ClientOnly>
        <GlobalOfflineBar />
        <Header :isStatic="true" :hasUnreadNotifications="getUnreadCount > 0" />
        <NavDesktop />
        <NavChips />
        <Notifications v-if="showNotifications" :notifications="getNotificationsData" />
        <GlobalPwaUpdateToast />
        <div v-if="route && isCACPage()" class="layout-shell">
            <main id="main" class="cac-page" tabindex="-1">
                <!-- Keyed on `path`, NOT `fullPath` (Asana O2-54). fullPath includes
                     the query string, so every filter change remounted the whole
                     page — which destroyed the modal state above it. Applying a
                     filter inside Edit Itinerary > Add Sites tore down the edit
                     modal, so the filtered list was never visible. Pages here read
                     the query reactively through useRouteQuery, so they do not need
                     a remount to notice it changed. -->
                <NuxtPage :page-key="(route) => route.path" class="page" />
            </main>
            <GlobalSiteFooter />
            <BlocksEmailSignupAlert v-if="!hasVisitedBefore && initialized" @closeEmailModal="closeEmailSignupModal" />
        </div>
        <div v-else class="layout-shell">
            <main id="main" tabindex="-1">
                <NuxtPage :page-key="(route) => route.path" class="page" />
            </main>
            <GlobalSiteFooter />
            <GlobalAddToItinerary v-if="addToItineraryIsOpen" :item="item" />
            <ItinerariesAddedSiteAlert v-if="siteAddedAlertIsOpen" @closeAddedSiteAlert="closeSiteAddedAlert" :siteName="siteNameForAlert" :listName="itineraryNameForAlert" />
            <GlobalAddedItineraryWithSiteAlert
                v-if="itineraryAddedWithSiteAlertIsOpen"
                @closeAddedItineraryWithSite="closeItineraryAddedWithSiteAlert"
                :siteName="siteNameForAlert"
                :listName="itineraryNameForAlert" />
            <BlocksEmailSignupAlert v-if="!hasVisitedBefore && initialized" @closeEmailModal="closeEmailSignupModal" />
        </div>
    </div>
</template>

<style lang="scss">
    @use '@/assets/styles/main.scss';

    :root {
        // Split out so the mobile chip bar (nav-chips.vue) can sit directly under
        // the fixed header and still have #main clear both. --sticky-bar-height
        // keeps its original meaning: total height of the fixed chrome on top.
        // 59px matches the header's own `height: $header_height`. It was 55px
        // here, which left the top 4px of #main tucked under the fixed header —
        // harmless while nothing sat there, but it would have clipped the chip
        // bar's top edge.
        --header-height: 59px;
        --chip-bar-height: 52px; // 36px chip + 8px above and below — see nav-chips.vue
        --nav-row-height: 0px;
        --sticky-bar-height: calc(var(--header-height) + var(--chip-bar-height) + var(--nav-row-height));

        // Chips are mobile-only per their ticket, so they stop at $tablet.
        @media (min-width: $tablet) {
            --chip-bar-height: 0px;
        }

        // The teal main nav and the taller white navbar are desktop-only (Figma
        // 4933-40092). Between $tablet and $desktop the header stays as it is and
        // navigation is the hamburger drawer.
        @media (min-width: $desktop) {
            --header-height: 88px;
            --nav-row-height: 62px;
        }
    }

    // Sticky footer, site-wide: on a short page the global footer sits on the
    // bottom of the viewport rather than leaving white space beneath it.
    .layout-shell {
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        min-height: 100dvh;
    }

    #main {
        // padding-top: 16px;
        margin-top: var(--sticky-bar-height);

        // `flex: 1 0 auto` is what pushes the footer down; it replaces the old
        // `padding-bottom: calc($bottom + env(safe-area-inset-bottom))`, which
        // existed solely to clear the fixed bottom navigation. That nav is gone
        // (Asana 1217333501985924), so the padding would now be a gap above the
        // footer rather than clearance.
        flex: 1 0 auto;
    }
</style>
